import React from 'react';
import { useSelector } from 'react-redux';
import Card from 'src/common/refactoredComponents/card/Card';
import {
    LeftColorCode,
    ContentContainer,
    HeaderSection,
    AccountName,
    AccountNumber,
    BodySection,
    FieldRow,
    FieldLabel,
    FieldValue,
    BottomSection,
    BottomField,
    BottomLabel,
    BottomValue,
    WarningIcon,
} from './ConsignedCard.styled';
import AlertCircleOutline from 'src/assets/images/AlertCircleOutline.svg';
import { useTranslation } from 'react-i18next';
import { BASE_TEXT_PX, FONT_FAMILY_DEMI, SEMANTIC_TEXT_PX, RED_FONT_COLOR, DISABLED_FONT_COLOR } from 'src/styles/fonts/MCFontConstants';

const getValue = (rowData, fields, fallback = '-') => {
    const value = fields
        .map((field) => rowData?.[field])
        .find((fieldValue) => fieldValue !== undefined && fieldValue !== null && fieldValue !== '');

    return value === undefined || value === null || value === '' ? fallback : value;
};

const formatAccountNumber = (value) => {
    if (!value || value === '-') return '';
    return String(value)
        .replace(/-/g, '')
        .replace(/^0+/, '');
};

const splitAccountValue = (value) => {
    const trimmedValue = String(value).trim();
    const accountParts = trimmedValue.match(/^([0-9-]+)\s*-?\s*(.*)$/);

    if (!accountParts) {
        return {
            accountNumber: formatAccountNumber(trimmedValue),
            accountName: '',
        };
    }

    return {
        accountNumber: formatAccountNumber(accountParts[1]),
        accountName: accountParts[2].trim(),
    };
};

const ConsignedCard = ({ data, index }) => {
    const accountValue = getValue(data, ['account'], '');
    const parsedAccount = accountValue ? splitAccountValue(accountValue) : null;
    const accountNameValue = parsedAccount?.accountName || getValue(data, ['accountName', 'customerName'], '');
    const accountNumberValue = parsedAccount?.accountNumber || formatAccountNumber(getValue(data, ['accountNumber', 'accountCode', 'customerNumber'], ''));
    
    const cfn = getValue(data, ['cfn', 'productCFN', 'productCfn']);
    const description = getValue(data, ['materialDesc', 'description', 'productDescription', 'itemDescription']);
    const lotSerial = getValue(data, ['lotSerial', 'lotSerialNumber', 'lotNumber', 'serialNumber']);
    const onHand = getValue(data, ['onHand', 'onHandQuantity', 'quantityOnHand', 'quantity']);
    const useByDate = getValue(data, ['useByDate', 'expirationDate', 'expiryDate']);
    const expiryStatus = getValue(data, ['expiryStatus', 'status'], '');
    
    const isExpired = expiryStatus === 'EXPIRED';
    const { t } = useTranslation();
    const { isLanguageChanged } = useSelector((state) => state.loginReducer);

    return (
        <Card
            id={`consigned-card-${index}`}
            mobileMargin="4px 20px 0px 15px"
            mobileMaxWidth="100%"
            padding="0px"
            height="fit-content"
            mbMarginBottom={'0px'}
            style={{ borderLeft: `4px solid ${isExpired ? RED_FONT_COLOR : DISABLED_FONT_COLOR}` }}
        >
            <ContentContainer>
                <HeaderSection>
                    <AccountName>{accountNameValue}</AccountName>
                    {accountNumberValue && (
                        <AccountNumber>{accountNumberValue}</AccountNumber>
                    )}
                </HeaderSection>
                
                <BodySection>
                    <FieldRow>
                        <FieldLabel fontFamily={FONT_FAMILY_DEMI} fontSize={BASE_TEXT_PX}>
                            {t('receiveSets.InventoryConsigned.cfn')}:
                            </FieldLabel>
                        <FieldValue fontSize={SEMANTIC_TEXT_PX}>
                            {cfn}
                        </FieldValue>
                    </FieldRow>
                    
                    <FieldRow noMargin>
                        <FieldValue>
                            {description}
                        </FieldValue>
                    </FieldRow>
                </BodySection>
                
                <BottomSection>
                    <BottomField>
                        <BottomLabel>
                            {isLanguageChanged 
                                ? t('reportUsage.record.reportPayment.ItemList.ReportPaymentItemListMobile.lotSerial') 
                                : t('receiveSets.InventoryConsigned.lot/serial')}
                            </BottomLabel>
                        <BottomValue>{lotSerial}</BottomValue>
                    </BottomField>
                    
                    <BottomField centered>
                        <BottomLabel>{t('receiveSets.InventoryConsigned.onHand')}</BottomLabel>
                        <BottomValue centered>{onHand}</BottomValue>
                    </BottomField>
                    
                    <BottomField>
                        <BottomLabel>{t('receiveSets.InventoryConsigned.useByDate')}</BottomLabel>
                        <BottomValue hasWarning={isExpired}>
                            {useByDate}
                            {isExpired && <WarningIcon src={AlertCircleOutline} alt="Warning" />}
                        </BottomValue>
                    </BottomField>
                </BottomSection>
            </ContentContainer>
        </Card>
    );
};

export default ConsignedCard;
